import FileSaver from 'file-saver';

const FileCategory = {
  IMAGE: 'image',
  VIDEO: 'video',
  OTHER: 'other',
};

const UNSUPPORTED_FILE_EXTENSIONS = ['caf'];

const JPEG_MIME_TYPE = 'image/jpeg';
const JPEG_EXTENSIONS = ['jpeg', 'jpg'];

const getFileExtension = (filename) => {
  const parts = filename.split('.');
  return parts[parts.length - 1];
};

const getFileName = (filename) => {
  const parts = filename.split('.');
  return parts.slice(0, parts.length - 1).join('.');
};

const getFileUrl = (file) => (file ? URL.createObjectURL(file) : null);

const isVideo = (file) => (file ? file.type.includes('video') : false);

const isImage = (file) => (file ? file.type.includes('image') : false);

const getFileCategory = (file) => {
  if (isVideo(file)) {
    return FileCategory.VIDEO;
  }
  if (isImage(file)) {
    return FileCategory.IMAGE;
  }
  return FileCategory.OTHER;
};

// Filter out unsupported file types and return the filtered array
// Also returns information about whether unsupported files were found
const filterUnsupportedFiles = (filesArray) => {
  let unsupportedFilesCount = 0;
  const filteredFiles = filesArray.filter((file) => {
    const fileName = file.name || '';
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension && UNSUPPORTED_FILE_EXTENSIONS.includes(extension)) {
      unsupportedFilesCount += 1;
      return false;
    }
    return true;
  });

  return {
    hasUnsupportedFiles: unsupportedFilesCount > 0,
    filteredFiles,
  };
};

const getUnsupportedFileTypes = () => UNSUPPORTED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(', ');

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  // GB matters for long-format session videos, which are routinely multi-gigabyte.
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

/**
 * The name a file should be saved under, corrected when its bytes are not what its name claims.
 *
 * Storage converts HEIC attachments to JPEG on the way out (see convertHeicIfNeeded), so keeping the
 * original `.heic` name would write JPEG bytes under an extension some apps refuse to open.
 *
 * @param {String} name Original file name
 * @param {String} mimeType MIME type of the bytes actually being saved
 * @return {String} File name to save under
 */
const getDownloadFileName = (name, mimeType) => {
  if (mimeType !== JPEG_MIME_TYPE || JPEG_EXTENSIONS.includes(getFileExtension(name).toLowerCase())) {
    return name;
  }
  const baseName = getFileName(name);
  return `${baseName || name}.${JPEG_EXTENSIONS[0]}`;
};

/**
 * Saves a blob to disk under the given name.
 *
 * file-saver owns the object URL it creates for the download and revokes it once the browser is
 * done with it, which a hand-rolled anchor has to remember to do itself.
 *
 * @param {Blob} blob File contents
 * @param {String} name File name to save under
 */
const downloadBlobAsFile = (blob, name) => FileSaver.saveAs(blob, getDownloadFileName(name, blob.type));

/**
 * Fetches a URL and saves the result to disk under the given name.
 *
 * Fetching to a blob first, rather than pointing an anchor at the URL, is what keeps the file name:
 * browsers ignore an anchor's `download` attribute for cross-origin sources, so a storage download
 * URL would otherwise open in a tab under whatever name the bucket reports.
 *
 * @param {String} url URL to download
 * @param {String} name File name to save under
 */
const downloadUrlAsFile = async (url, name) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not download the file (${response.status})`);
  }
  return downloadBlobAsFile(await response.blob(), name);
};

export {
  FileCategory,
  getFileExtension,
  getFileName,
  getFileUrl,
  isVideo,
  isImage,
  getFileCategory,
  filterUnsupportedFiles,
  getUnsupportedFileTypes,
  formatFileSize,
  getDownloadFileName,
  downloadBlobAsFile,
  downloadUrlAsFile,
};
